import React from 'react';

interface EmailDirectoryEntry {
  address: string;
  purpose: string;
  source: string;
  tierKey?: 'shared_role_mailboxes' | 'named_user_mailboxes';
}

interface MailboxSignal {
  email: string;
  has_mail: boolean;
  has_unread_imap?: boolean;
  imap_unseen_count?: number;
  imap_messages_count?: number;
  last_event_type?: string | null;
  last_event_at?: string | null;
  provider?: string | null;
}

interface DashboardEmailAccountsProps {
  language: string;
  adminEmailInboxUrl: string;
  accessToken?: string;
  gatewayBaseUrl: string;
  buildInboxUrlForAddress: (address: string) => string;
  emailDirectoryEntries: EmailDirectoryEntry[];
  currentUserEmail?: string;
  showAdminAudit?: boolean;
  onRefreshAccounts?: () => void;
}

const DashboardEmailAccounts: React.FC<DashboardEmailAccountsProps> = ({
  language,
  adminEmailInboxUrl,
  accessToken,
  gatewayBaseUrl,
  buildInboxUrlForAddress,
  emailDirectoryEntries,
  currentUserEmail,
  showAdminAudit,
  onRefreshAccounts,
}) => {
  const ALERT_CONFIG_KEY = 'nextcompli.alerting.external.config.v1';
  const ALERT_AUDIT_KEY = 'nextcompli.alerting.external.audit.v1';
  const FORWARD_ALLOWLIST_KEY = 'nextcompli.alerting.external.allowlist.v1';

  const categoryLabels = React.useMemo(
    () => ({
      critical: language === 'es' ? 'Alertas críticas' : 'Critical alerts',
      risk: language === 'es' ? 'Alertas de riesgo de cumplimiento' : 'Compliance risk alerts',
      weekly: language === 'es' ? 'Resumen semanal' : 'Weekly digest',
      release: language === 'es' ? 'Lanzamientos e integraciones' : 'Release updates',
    }),
    [language],
  );

  const [alertDestination, setAlertDestination] = React.useState('');
  const [forwardDestination, setForwardDestination] = React.useState('');
  const [verifiedForwardDestinations, setVerifiedForwardDestinations] = React.useState<string[]>([]);
  const [verificationNotice, setVerificationNotice] = React.useState<string | null>(null);
  const [routingFeedback, setRoutingFeedback] = React.useState<string | null>(null);
  const [auditEvents, setAuditEvents] = React.useState<Array<{ at: string; actor: string; action: string; details: string }>>([]);
  const [categoryRouting, setCategoryRouting] = React.useState<Record<string, 'primary' | 'forward'>>({
    critical: 'primary',
    risk: 'primary',
    weekly: 'primary',
    release: 'primary',
  });
  const [mailboxSignals, setMailboxSignals] = React.useState<Record<string, MailboxSignal>>({});
  const [mailboxSignalFeedback, setMailboxSignalFeedback] = React.useState<string | null>(null);
  const [clearingMailboxAddress, setClearingMailboxAddress] = React.useState<string | null>(null);

  const allowedDomains = React.useMemo(() => {
    const configured = (process.env.REACT_APP_ALERT_FORWARD_ALLOWED_DOMAINS || '').trim();
    if (configured) {
      return configured
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);
    }
    const persisted = window.localStorage.getItem(FORWARD_ALLOWLIST_KEY);
    if (persisted) {
      return persisted
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);
    }
    return ['nextcompli.com', 'nextcompli.ai', 'gmail.com', 'outlook.com', 'yahoo.com', 'icloud.com'];
  }, []);

  const appendAuditEvent = React.useCallback((action: string, details: string) => {
    const actor = String(currentUserEmail || '').trim().toLowerCase() || 'unknown-user';
    const next = [{ at: new Date().toISOString(), actor, action, details }, ...auditEvents].slice(0, 60);
    setAuditEvents(next);
    window.localStorage.setItem(ALERT_AUDIT_KEY, JSON.stringify(next));
  }, [auditEvents, currentUserEmail]);

  React.useEffect(() => {
    const raw = window.localStorage.getItem(ALERT_CONFIG_KEY);
    if (!raw) {
      setAlertDestination(String(currentUserEmail || '').trim().toLowerCase());
      return;
    }
    try {
      const parsed = JSON.parse(raw) as {
        alertDestination?: string;
        forwardDestination?: string;
        verifiedForwardDestinations?: string[];
        categoryRouting?: Record<string, 'primary' | 'forward'>;
      };
      setAlertDestination(String(parsed.alertDestination || currentUserEmail || '').trim().toLowerCase());
      setForwardDestination(String(parsed.forwardDestination || '').trim().toLowerCase());
      setVerifiedForwardDestinations(Array.isArray(parsed.verifiedForwardDestinations) ? parsed.verifiedForwardDestinations : []);
      if (parsed.categoryRouting) {
        setCategoryRouting((current) => ({ ...current, ...parsed.categoryRouting }));
      }
    } catch {
      setAlertDestination(String(currentUserEmail || '').trim().toLowerCase());
    }

    const rawAudit = window.localStorage.getItem(ALERT_AUDIT_KEY);
    if (rawAudit) {
      try {
        const parsedAudit = JSON.parse(rawAudit) as Array<{ at: string; actor: string; action: string; details: string }>;
        if (Array.isArray(parsedAudit)) {
          setAuditEvents(parsedAudit.slice(0, 60));
        }
      } catch {
      }
    }
  }, [currentUserEmail]);

  const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const isAllowedForwardDomain = (value: string): boolean => {
    const normalized = value.trim().toLowerCase();
    const parts = normalized.split('@');
    if (parts.length !== 2) {
      return false;
    }
    const domain = parts[1];
    return allowedDomains.includes(domain);
  };

  const verifyForwardDestination = () => {
    const normalized = forwardDestination.trim().toLowerCase();
    if (!normalized) {
      setVerificationNotice(language === 'es' ? 'Ingresa un destino externo para verificar.' : 'Enter an external destination to verify.');
      return;
    }
    if (!isValidEmail(normalized)) {
      setVerificationNotice(language === 'es' ? 'El correo de reenvío no es válido.' : 'Forward destination is not a valid email address.');
      return;
    }
    if (!isAllowedForwardDomain(normalized)) {
      setVerificationNotice(
        language === 'es'
          ? `Dominio no permitido. Permitidos: ${allowedDomains.join(', ')}`
          : `Domain is not allowlisted. Allowed domains: ${allowedDomains.join(', ')}`,
      );
      return;
    }
    if (!verifiedForwardDestinations.includes(normalized)) {
      const next = [...verifiedForwardDestinations, normalized];
      setVerifiedForwardDestinations(next);
      setVerificationNotice(language === 'es' ? 'Destino verificado para reenvío.' : 'Destination verified for forwarding.');
      appendAuditEvent('forward_destination_verified', normalized);
      return;
    }
    setVerificationNotice(language === 'es' ? 'Destino ya verificado.' : 'Destination is already verified.');
  };

  const saveExternalAlertingConfig = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedAlert = alertDestination.trim().toLowerCase();
    const normalizedForward = forwardDestination.trim().toLowerCase();

    if (!isValidEmail(normalizedAlert)) {
      setRoutingFeedback(language === 'es' ? 'El correo principal de alertas no es válido.' : 'Primary alert destination must be a valid email address.');
      return;
    }
    if (normalizedForward) {
      if (!isValidEmail(normalizedForward)) {
        setRoutingFeedback(language === 'es' ? 'El destino de reenvío no es válido.' : 'Forward destination must be a valid email address.');
        return;
      }
      if (!isAllowedForwardDomain(normalizedForward)) {
        setRoutingFeedback(
          language === 'es'
            ? `Dominio de reenvío no permitido. Permitidos: ${allowedDomains.join(', ')}`
            : `Forward domain is not allowlisted. Allowed domains: ${allowedDomains.join(', ')}`,
        );
        return;
      }
      if (!verifiedForwardDestinations.includes(normalizedForward)) {
        setRoutingFeedback(language === 'es' ? 'Verifica el destino de reenvío antes de guardar.' : 'Verify the forward destination before saving.');
        return;
      }
    }

    const payload = {
      alertDestination: normalizedAlert,
      forwardDestination: normalizedForward,
      verifiedForwardDestinations,
      categoryRouting,
      updatedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(ALERT_CONFIG_KEY, JSON.stringify(payload));
    setRoutingFeedback(language === 'es' ? 'Configuración de alertas y reenvío guardada.' : 'Alert and forwarding settings saved.');
    appendAuditEvent('external_alerting_config_saved', `${normalizedAlert} | forward=${normalizedForward || 'none'}`);
  };

  const groups = React.useMemo(() => {
    const byTier = {
      shared_role_mailboxes: [] as EmailDirectoryEntry[],
      named_user_mailboxes: [] as EmailDirectoryEntry[],
    };

    for (const entry of emailDirectoryEntries || []) {
      const tier = entry.tierKey || 'named_user_mailboxes';
      byTier[tier].push(entry);
    }

    return byTier;
  }, [emailDirectoryEntries]);

  const tierHeaders = {
    shared_role_mailboxes: language === 'es' ? 'Nivel 1: buzones compartidos por rol (RBAC)' : 'Tier 1: shared role mailboxes (RBAC)',
    named_user_mailboxes: language === 'es' ? 'Nivel 2: buzones de usuario nominal' : 'Tier 2: named user mailboxes',
  };

  const orderedTiers: Array<'shared_role_mailboxes' | 'named_user_mailboxes'> = [
    'shared_role_mailboxes',
    'named_user_mailboxes',
  ];

  const [expandedTiers, setExpandedTiers] = React.useState<Record<'shared_role_mailboxes' | 'named_user_mailboxes', boolean>>({
    shared_role_mailboxes: false,
    named_user_mailboxes: false,
  });

  const [showAlertsForwarding, setShowAlertsForwarding] = React.useState(false);

  const toggleTier = (tierKey: 'shared_role_mailboxes' | 'named_user_mailboxes') => {
    setExpandedTiers((current) => ({ ...current, [tierKey]: !current[tierKey] }));
  };

  const loadMailboxSignals = React.useCallback(async () => {
    const requestedEmails = [...new Set((emailDirectoryEntries || []).map((entry) => String(entry.address || '').trim().toLowerCase()).filter(Boolean))];
    if (!accessToken || !gatewayBaseUrl || requestedEmails.length === 0) {
      setMailboxSignals({});
      return;
    }

    const params = new URLSearchParams();
    requestedEmails.forEach((email) => params.append('email', email));

    try {
      const response = await fetch(`${gatewayBaseUrl.replace(/\/$/, '')}/api/v1/email-operations/mailbox-signals?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Request failed (${response.status}).`);
      }
      const payload = (await response.json()) as { signals?: MailboxSignal[] };
      const nextSignals = new Map<string, MailboxSignal>();
      (payload.signals || []).forEach((signal) => {
        const normalizedEmail = String(signal.email || '').trim().toLowerCase();
        if (normalizedEmail) {
          nextSignals.set(normalizedEmail, { ...signal, email: normalizedEmail });
        }
      });
      setMailboxSignals(Object.fromEntries(nextSignals.entries()));
      setMailboxSignalFeedback(null);
    } catch {
      setMailboxSignalFeedback(language === 'es' ? 'No se pudo cargar el estado visual de buzones.' : 'Unable to load mailbox status signals right now.');
    }
  }, [accessToken, emailDirectoryEntries, gatewayBaseUrl, language]);

  React.useEffect(() => {
    void loadMailboxSignals();
  }, [loadMailboxSignals]);

  const clearMailboxSignal = React.useCallback(
    async (address: string) => {
      const normalizedAddress = String(address || '').trim().toLowerCase();
      if (!accessToken || !gatewayBaseUrl || !normalizedAddress) {
        return;
      }
      setClearingMailboxAddress(normalizedAddress);
      try {
        const response = await fetch(
          `${gatewayBaseUrl.replace(/\/$/, '')}/api/v1/email-operations/mailbox-signals/${encodeURIComponent(normalizedAddress)}/clear`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        if (!response.ok) {
          throw new Error(`Request failed (${response.status}).`);
        }
        const payload = (await response.json()) as { signal?: MailboxSignal };
        setMailboxSignals((current) => ({
          ...current,
          [normalizedAddress]: payload.signal
            ? { ...payload.signal, email: normalizedAddress }
            : {
                email: normalizedAddress,
                has_mail: false,
                last_event_type: 'cleared',
                last_event_at: new Date().toISOString(),
                provider: 'nextcompli',
              },
        }));
        setMailboxSignalFeedback(language === 'es' ? 'Buzón marcado como vacío.' : 'Mailbox marked as empty.');
      } catch {
        setMailboxSignalFeedback(language === 'es' ? 'No se pudo limpiar la señal del buzón.' : 'Unable to clear the mailbox signal right now.');
      } finally {
        setClearingMailboxAddress(null);
      }
    },
    [accessToken, gatewayBaseUrl, language],
  );

  const mailWaitingCount = Object.values(mailboxSignals).filter((signal) => signal.has_mail || (signal.imap_unseen_count || 0) > 0).length;
  const unreadCounts = Object.values(mailboxSignals).map((signal) => Math.max(0, Number(signal.imap_unseen_count || 0))).filter((count) => count > 0);
  const sharedInbox = unreadCounts.length > 1 && new Set(unreadCounts).size === 1;
  const totalUnreadCount = sharedInbox ? unreadCounts[0] : unreadCounts.reduce((sum, count) => sum + count, 0);
  const handleRefreshAccounts = () => {
    if (onRefreshAccounts) {
      onRefreshAccounts();
    }
    void loadMailboxSignals();
  };

  return (
    <section className="dashboard-settings-sections dashboard-settings-sections-centered" aria-label="Email accounts">
      <article className="card settings-card">
        <div className="settings-card-header">
          <h2>{language === 'es' ? 'Cuentas de correo' : 'Email Accounts'}</h2>
        </div>
        <p className="dashboard-panel-subtitle">
          {language === 'es'
            ? 'Accede a cuentas por nivel operativo: buzones compartidos por rol y buzones de usuario nominal.'
            : 'Access accounts by operational tier: shared role mailboxes and named user mailboxes.'}
        </p>
        <div style={{ marginTop: '0.75rem', padding: '0.75rem 0.9rem', borderRadius: '0.6rem', background: mailWaitingCount > 0 ? 'rgba(177, 62, 24, 0.12)' : 'rgba(17, 93, 67, 0.08)' }}>
          <strong>{language === 'es' ? 'Señal visual de buzones' : 'Mailbox visual signal'}</strong>
          <p style={{ margin: '0.35rem 0 0' }}>
            {mailWaitingCount > 0
              ? (language === 'es'
                ? `${mailWaitingCount} dirección(es) con correo pendiente${totalUnreadCount > 0 ? ` (${totalUnreadCount} no leído${sharedInbox ? ' en la bandeja compartida' : ''})` : ''}.`
                : `${mailWaitingCount} address(es) with waiting mail${totalUnreadCount > 0 ? ` (${totalUnreadCount} unread${sharedInbox ? ' in the shared inbox' : ''})` : ''}.`)
              : (language === 'es' ? 'No hay buzones marcados con correo pendiente.' : 'No mailboxes are currently flagged with waiting mail.')}
          </p>
          <p style={{ margin: '0.35rem 0 0', fontSize: '0.84rem', opacity: 0.78 }}>
            {language === 'es'
              ? 'La señal usa los eventos entrantes "received". Se limpia cuando alguien autorizado confirma que el buzón ya fue revisado.'
              : 'This signal uses inbound "received" events. It clears when an authorized viewer confirms review.'}
          </p>
        </div>
        {mailboxSignalFeedback ? <p className="dashboard-form-success" style={{ marginTop: '0.75rem' }}>{mailboxSignalFeedback}</p> : null}

        <div style={{ marginTop: '1rem' }}>
          <button
            type="button"
            onClick={() => setShowAlertsForwarding((prev) => !prev)}
            aria-expanded={showAlertsForwarding}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '0.6rem',
              margin: 0,
              padding: '0.65rem 0.8rem',
              border: '1px solid rgba(0,0,0,0.12)',
              borderRadius: showAlertsForwarding ? '0.55rem 0.55rem 0 0' : '0.55rem',
              background: 'rgba(0,0,0,0.02)',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontWeight: 700 }}>
              {language === 'es' ? 'Alertas externas y reglas de reenvío' : 'External alerts and forwarding rules'}
            </span>
            <span style={{ fontSize: '0.9rem', opacity: 0.72 }}>{showAlertsForwarding ? '▾' : '▸'}</span>
          </button>
          {showAlertsForwarding ? (
        <form style={{ padding: '0.9rem', border: '1px solid rgba(0,0,0,0.12)', borderTop: 'none', borderRadius: '0 0 0.55rem 0.55rem' }} onSubmit={saveExternalAlertingConfig}>
          <p style={{ margin: '0 0 0.6rem', fontSize: '0.88rem', opacity: 0.78 }}>
            {language === 'es'
              ? 'Incluye verificación de destino, allowlist de dominios, rutas por categoría y registro de auditoría.'
              : 'Includes destination verification, domain allowlist, category routing, and audit logging.'}
          </p>
          <div style={{ display: 'grid', gap: '0.55rem' }}>
            <label style={{ display: 'grid', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.84rem', opacity: 0.82 }}>{language === 'es' ? 'Destino primario de alertas' : 'Primary alert destination'}</span>
              <input
                type="email"
                value={alertDestination}
                onChange={(event) => setAlertDestination(event.target.value)}
                placeholder={language === 'es' ? 'equipo-riesgo@empresa.com' : 'risk-team@example.com'}
                required
              />
            </label>
            <label style={{ display: 'grid', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.84rem', opacity: 0.82 }}>{language === 'es' ? 'Destino de reenvío externo (opcional)' : 'External forwarding destination (optional)'}</span>
              <input
                type="email"
                value={forwardDestination}
                onChange={(event) => setForwardDestination(event.target.value)}
                placeholder={language === 'es' ? 'alertas-external@dominio.com' : 'external-alerts@domain.com'}
              />
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
              <button type="button" className="dashboard-btn dashboard-btn-secondary" onClick={verifyForwardDestination}>
                {language === 'es' ? 'Verificar destino' : 'Verify destination'}
              </button>
              <span style={{ fontSize: '0.8rem', opacity: 0.75 }}>
                {language === 'es' ? 'Dominios permitidos' : 'Allowed domains'}: {allowedDomains.join(', ')}
              </span>
            </div>
            {verificationNotice ? <p className="dashboard-form-success" style={{ marginTop: 0 }}>{verificationNotice}</p> : null}

            <div style={{ display: 'grid', gap: '0.4rem', marginTop: '0.35rem' }}>
              <strong>{language === 'es' ? 'Ruteo por categoría de alerta' : 'Per-category alert routing'}</strong>
              {Object.entries(categoryLabels).map(([key, label]) => (
                <label key={`alert-route-${key}`} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                  <span style={{ minWidth: 220 }}>{label}</span>
                  <select
                    value={categoryRouting[key] || 'primary'}
                    onChange={(event) => setCategoryRouting((current) => ({ ...current, [key]: event.target.value as 'primary' | 'forward' }))}
                  >
                    <option value="primary">{language === 'es' ? 'Destino primario' : 'Primary destination'}</option>
                    <option value="forward">{language === 'es' ? 'Destino de reenvío externo' : 'External forward destination'}</option>
                  </select>
                </label>
              ))}
            </div>

            {routingFeedback ? <p className="dashboard-form-success" style={{ marginTop: 0 }}>{routingFeedback}</p> : null}
            <div className="dashboard-action-buttons" style={{ marginTop: '0.2rem' }}>
              <button type="submit" className="dashboard-btn dashboard-btn-primary">
                {language === 'es' ? 'Guardar alertas y reenvío' : 'Save alerts and forwarding'}
              </button>
            </div>
          </div>
        </form>
          ) : null}
        </div>

        {showAdminAudit ? (
          <div style={{ marginTop: '1rem', padding: '0.85rem', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '0.6rem' }}>
            <h3 style={{ margin: '0 0 0.55rem' }}>{language === 'es' ? 'Registro de auditoría (alertas/reenvío)' : 'Audit log (alerts/forwarding)'}</h3>
            {auditEvents.length === 0 ? (
              <p style={{ margin: 0, opacity: 0.75 }}>{language === 'es' ? 'Sin eventos todavía.' : 'No events yet.'}</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                {auditEvents.slice(0, 15).map((event, index) => (
                  <li key={`audit-${event.at}-${index}`} style={{ marginBottom: '0.35rem' }}>
                    <strong>{new Date(event.at).toLocaleString()}</strong> — {event.actor} — {event.action} — {event.details}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {onRefreshAccounts ? (
          <div className="dashboard-action-buttons" style={{ marginTop: '0.75rem' }}>
            <button type="button" className="dashboard-btn dashboard-btn-secondary" onClick={handleRefreshAccounts}>
              {language === 'es' ? 'Actualizar lista' : 'Refresh list'}
            </button>
          </div>
        ) : null}
        <div style={{ marginTop: '1.5rem' }}>
          {emailDirectoryEntries && emailDirectoryEntries.length > 0 ? (
            orderedTiers.map((tierKey) => {
              const entries = groups[tierKey];
              if (!entries.length) {
                return null;
              }

              const isExpanded = expandedTiers[tierKey];
              const contentId = `mailbox-tier-${tierKey}`;

              return (
                <div key={tierKey} style={{ marginBottom: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => toggleTier(tierKey)}
                    aria-expanded={isExpanded}
                    aria-controls={contentId}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '0.6rem',
                      margin: 0,
                      padding: '0.65rem 0.8rem',
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: '0.55rem',
                      background: 'rgba(0,0,0,0.02)',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontWeight: 700, textAlign: 'left' }}>{tierHeaders[tierKey]} ({entries.length})</span>
                    <span style={{ fontSize: '0.9rem', opacity: 0.72 }}>{isExpanded ? '▾' : '▸'}</span>
                  </button>
                  {isExpanded ? (
                  <div id={contentId} style={{ marginTop: '0.55rem' }}>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {entries.map((entry) => {
                      const signal = mailboxSignals[String(entry.address || '').trim().toLowerCase()];
                      const unreadCount = Math.max(0, Number(signal?.imap_unseen_count || 0));
                      const hasMail = Boolean(signal?.has_mail) || unreadCount > 0;
                      const isClearing = clearingMailboxAddress === String(entry.address || '').trim().toLowerCase();
                      return (
                        <li
                          key={`email-account-${entry.address}`}
                          style={{
                            marginBottom: '1rem',
                            padding: '0.75rem',
                            background: 'rgba(0,0,0,0.03)',
                            borderRadius: '0.5rem',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                                <strong>{entry.address}</strong>
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '0.15rem 0.5rem',
                                    borderRadius: '999px',
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    background: hasMail ? 'rgba(177, 62, 24, 0.14)' : 'rgba(17, 93, 67, 0.12)',
                                    color: hasMail ? '#8b2f12' : '#115d43',
                                  }}
                                >
                                  {hasMail
                                    ? (language === 'es'
                                      ? (unreadCount > 0 ? `${unreadCount} no leído(s)` : 'Correo pendiente')
                                      : (unreadCount > 0 ? `${unreadCount} unread` : 'Mail waiting'))
                                    : (language === 'es' ? 'Vacío' : 'Empty')}
                                </span>
                              </div>
                              <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', opacity: 0.7 }}>{entry.purpose}</p>
                              <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', opacity: 0.68 }}>
                                {language === 'es' ? 'Dirección' : 'Address'}: {entry.address}
                              </p>
                              {signal?.last_event_at ? (
                                <p style={{ margin: '0.3rem 0 0', fontSize: '0.8rem', opacity: 0.72 }}>
                                  {language === 'es' ? 'Última señal' : 'Last signal'}: {new Date(signal.last_event_at).toLocaleString()}
                                </p>
                              ) : null}
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginLeft: '1rem' }}>
                              {hasMail ? (
                                <button
                                  type="button"
                                  className="dashboard-btn dashboard-btn-secondary"
                                  onClick={() => { void clearMailboxSignal(entry.address); }}
                                  disabled={isClearing}
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  {isClearing
                                    ? (language === 'es' ? 'Limpiando...' : 'Clearing...')
                                    : (language === 'es' ? 'Marcar vacío' : 'Mark empty')}
                                </button>
                              ) : null}
                              <a
                                href={buildInboxUrlForAddress(entry.address)}
                                target="_blank"
                                rel="noreferrer"
                                className="dashboard-btn dashboard-btn-secondary"
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                {language === 'es' ? 'Abrir' : 'Open'}
                              </a>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  </div>
                  ) : null}
                </div>
              );
            })
          ) : (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li>{language === 'es' ? 'No hay cuentas de correo configuradas.' : 'No email accounts configured.'}</li>
            </ul>
          )}
        </div>
        {adminEmailInboxUrl ? (
          <div className="dashboard-action-buttons" style={{ marginTop: '1.5rem' }}>
            <a href={adminEmailInboxUrl} target="_blank" rel="noreferrer" className="dashboard-btn dashboard-btn-primary">
              {language === 'es' ? 'Abrir Gmail' : 'Open Gmail'}
            </a>
          </div>
        ) : null}
      </article>
    </section>
  );
};

export default DashboardEmailAccounts;
